import { type RefSelectProps } from 'antd'
import { useAtomValue } from 'jotai'
import { type Ref, forwardRef, useMemo } from 'react'

import { Select } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import { STATUSES } from '@/constants/common/message.constants'
import { type PartnerEntity } from '@/libs/api'
import { useSelectOnChangeHandler } from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { usePartnersSelectQuery } from '../../hooks'

import { mapToPartnerFieldName } from './map-to-partner-field-name'

type Props = SingleOrMultipleSelectProps<number, PartnerEntity> & {
  tenantKey: string
  /** 拠点ID指定（指定しない場合は選択された拠点IDを使用して絞り込まれる） */
  dcId?: number
}

/**
 * パートナーを選択するSelect（複数選択）
 */
export const PartnerSelect = forwardRef(
  (
    {
      placeholder = STATUSES.UNSELECTED,
      mode,
      onChange,
      tenantKey,
      dcId,
      value,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const selectedDcId = useAtomValue(selectedDcIdAtom)
    const { data, isFetching, isLoading } = usePartnersSelectQuery(tenantKey, {
      dcId: dcId ?? selectedDcId
    })

    const options = useMemo(() => {
      return data?.map((partner) => ({
        ...partner,
        // nameで指定すると利用側で取得できるoption.nameの値が変わるため、labelで指定
        label: mapToPartnerFieldName(partner)
      }))
    }, [data])

    const handleChange = useSelectOnChangeHandler({ mode, onChange })

    return (
      <Select
        customRef={ref}
        mode={mode}
        value={isLoading ? null : value}
        placeholder={placeholder}
        options={options}
        loading={isFetching}
        onChange={handleChange}
        {...props}
      />
    )
  }
)
